import React from 'react';
import { palettes, Theme, MediumTitle, TinyBody, RegularTitle } from '@indigo-ag/component-module';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { faCaretRight, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';

import { createStylesHook } from 'shared/hooks/createStylesHook';

/**
 * List the possible statuses of a phase
 */
export enum StepStatus {
  COMPLETED,
  DENIED,
  UNCOMPLETED
}

export interface IStep {
  id: string;
  title: string;
  subtitle?: string;
  visited?: boolean;
  path?: string;
  disabled?: boolean;
  subSteps?: IStep[];
  completed?: boolean;
  status: StepStatus;
  component?: React.ReactNode;
  contentComponent?: React.ReactNode;
}

export interface StepProps {
  step: IStep;
  isActive: boolean;
  stepNumber?: number;
  isSubStep?: boolean;
  onStepClick?: (step: IStep) => void;
}

const useStyles = createStylesHook((theme: Theme) => ({
  clickable: {
    cursor: 'pointer'
  },
  currentStepIcon: {
    color: palettes.coastal.accent.green,
    fontSize: '1.5rem',
    marginRight: -8
  },
  stepCircle: {
    alignItems: 'center',
    background: palettes.neutral.white,
    border: `1px solid ${palettes.neutral[800]}`,
    borderRadius: '50%',
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
    textAlign: 'center',
    width: 32
  },
  stepCircleContainer: {
    alignItems: 'center',
    display: 'flex',
    height: 'fit-content',
    minWidth: 65
  },
  stepContainer: {
    display: 'flex',
    background: 'none',
    border: 'none',
    padding: 0,
    margin: 0
  },
  stepContent: {
    height: 32,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  },
  stepInBetween: {
    '& $stepCircle, & $stepCircleContainer': {
      '& svg[class*="fa-check"]': {
        fontSize: theme.typography.pxToRem(10)
      },
      height: 20,
      width: 20
    },
    '& $stepContent': {
      '& $stepTitle': {
        color: palettes.neutral[700],
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 'normal'
      },
      height: 20
    }
  },
  stepNumber: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 600
  },
  stepSubtitle: {
    color: `${palettes.neutral[700]} !important`,
    marginTop: '4px !important',
    textAlign: 'left'
  },
  stepTitle: {
    color: palettes.neutral[900],
    textAlign: 'left'
  },
  // statuses
  stepCompleted: {
    '& $stepCircle': {
      borderColor: palettes.coastal.accent.green,
      background: palettes.coastal.accent.green,
      color: palettes.neutral.white
    }
  },
  stepDenied: {
    '& $stepCircle': {
      borderColor: palettes.coastal.accent.red,
      background: palettes.coastal.accent.red,
      color: palettes.neutral.white
    }
  },
  stepDisabled: {
    '& $stepCircle': {
      borderColor: palettes.neutral[500]
    },
    '& $stepNumber': {
      color: palettes.neutral[500]
    },
    '& $stepTitle': {
      fontWeight: 'normal',
      color: palettes.neutral[700]
    }
  }
}));

export const Step: React.FunctionComponent<StepProps> = ({
  step,
  isActive,
  stepNumber,
  isSubStep = false,
  onStepClick
}) => {
  /**
   * Styles
   */
  const classes = useStyles();

  const notifyStepClicked = () => {
    if (onStepClick) {
      onStepClick(step);
    }
  };

  const stepClasses = `${classes.stepContainer}
    ${!step.disabled && onStepClick ? classes.clickable : step.disabled ? classes.stepDisabled : ''}
    ${isSubStep ? classes.stepInBetween : ''}
    
    ${step.status === StepStatus.COMPLETED ? classes.stepCompleted : ''}
    ${step.status === StepStatus.DENIED ? classes.stepDenied : ''}
    ${step.status === StepStatus.UNCOMPLETED ? '' : ''}
  `;

  const renderStatus = () => {
    switch (step.status) {
      case StepStatus.COMPLETED:
        return <FontAwesomeIcon icon={faCheck} />;

      case StepStatus.DENIED:
        return <FontAwesomeIcon icon={faTimes} />;

      case StepStatus.UNCOMPLETED:
      default:
        return <MediumTitle className={classes.stepNumber}>{stepNumber}</MediumTitle>;
    }
  };

  return (
    <button className={stepClasses} disabled={step.disabled} onClick={notifyStepClicked}>
      <div className={`${classes.stepCircleContainer}`}>
        {isActive ? <FontAwesomeIcon icon={faCaretRight} className={classes.currentStepIcon} /> : null}
        {/* status rendered*/}
        <div className={`${classes.stepCircle}`}>{renderStatus()}</div>
      </div>
      <div className={`${classes.stepContent}`}>
        <RegularTitle className={`text-transform-capitalize ${classes.stepTitle}`}>{step.title}</RegularTitle>
        {step.subtitle ? <TinyBody className={classes.stepSubtitle}>{step.subtitle}</TinyBody> : null}
      </div>
    </button>
  );
};
